import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { ButtonComp, MoneyInputComp } from "../../../../components";
import { ModalCompAdvance } from "../../../../components/Ui/ModalComp";
import { useInitializeSmsTopupMutation } from "../../../../store/admin/communicationSlice";
import { formatMoney } from "../../../../utils";

const schema = Yup.object({
  amount: Yup.number()
    .transform((value, original) => {
      if (original === "" || original == null) return undefined;
      const n = typeof original === "string" ? parseFloat(original.replace(/,/g, "")) : Number(original);
      return Number.isFinite(n) ? n : undefined;
    })
    .typeError("Enter a valid amount")
    .required("Amount is required")
    .min(100, "Minimum top-up is NGN 100"),
});

/**
 * SMS wallet top-up: enter amount → online payment → API credits branch wallet on success.
 */
export default function SmsTopupModal({ show, onClose }) {
  const [initSmsTopup, { isLoading }] = useInitializeSmsTopupMutation();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    const amount = Number(values.amount);
    if (!Number.isFinite(amount) || amount < 100) {
      toast.error("Enter a valid amount of at least NGN 100.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await initSmsTopup({
        amount_naira: amount,
        frontend_base_url: window.location.origin,
      }).unwrap();
      if (res?.payment_url) {
        toast.success("Redirecting to online payment…");
        window.location.href = res.payment_url;
        return;
      }
      toast.error("No payment URL returned.");
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Could not start payment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const busy = isLoading || submitting;

  return (
    <ModalCompAdvance
      show={show}
      handleClose={onClose}
      size="md"
      centered
      close
      arrayComp={[
        {
          name: "Top up SMS",
          component: (
            <div className="px-1 pb-2">
              <p className="mb-3" style={{ fontSize: "14px", color: "#525252" }}>
                Enter how much you want to add to your SMS balance. You will complete
                payment online. When payment succeeds, the amount is credited to
                this branch immediately.
              </p>
              <Formik
                initialValues={{ amount: "" }}
                validationSchema={schema}
                onSubmit={handleSubmit}
              >
                {({ values }) => (
                  <Form>
                    <div className="mb-3">
                      <MoneyInputComp
                        name="amount"
                        LabelText="Amount (NGN)"
                        LabelClassName="fw-semibold small"
                        placeholder="e.g. 5,000"
                        desc="Minimum NGN 100"
                      />
                    </div>
                    {values.amount && Number(values.amount) >= 100 && (
                      <p className="small mb-3" style={{ color: "#31778c" }}>
                        You will pay{" "}
                        <strong>
                          {formatMoney(Number(values.amount).toFixed(2), true)}
                        </strong>{" "}
                        and receive the same amount as SMS credit.
                      </p>
                    )}
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <ButtonComp
                        type="button"
                        btnText="Cancel"
                        btnClassName="border border-primary1 text-primary1 px-4 py-2 rounded bg-white"
                        onClick={onClose}
                        disabled={busy}
                      />
                      <ButtonComp
                        type="submit"
                        btnText={busy ? "Please wait…" : "Pay online"}
                        btnClassName="bg-primary1 text-white px-4 py-2 rounded border-0"
                        disabled={busy}
                        loading={busy}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          ),
        },
      ]}
      modalBody={0}
    />
  );
}
